/**
 * Email Health Dashboard Page
 * 
 * Phase E1.4: Email Delivery Monitoring
 * 
 * Features:
 * - Success/fail stats (24h/7d)
 * - Failure breakdown by reason
 * - Logs table with filters and row expansion
 * 
 * RBAC: SUPER_ADMIN, HR_MANAGER (full), others need EMAIL_LOG_VIEW permission
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { usePermission, Permission } from '@/contexts/PermissionContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  Mail,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  RefreshCw,
  Search,
  ChevronDown,
  ChevronUp,
  Filter,
  Activity,
  Inbox
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status badge colors
const STATUS_COLORS = {
  sent: 'bg-green-100 text-green-800 border-green-200',
  delivered: 'bg-green-100 text-green-800 border-green-200',
  pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  retrying: 'bg-blue-100 text-blue-800 border-blue-200',
  failed: 'bg-red-100 text-red-800 border-red-200',
  bounced: 'bg-red-100 text-red-800 border-red-200',
  cancelled: 'bg-gray-100 text-gray-800 border-gray-200'
};

// Failure reason labels
const FAILURE_REASON_LABELS = {
  smtp_error: 'SMTP Error',
  auth_failed: 'Authentication Failed',
  recipient_rejected: 'Recipient Rejected',
  sender_rejected: 'Sender Rejected',
  network_error: 'Network Error',
  rate_limited: 'Rate Limited',
  invalid_address: 'Invalid Address',
  content_rejected: 'Content Rejected',
  timeout: 'Connection Timeout',
  unknown: 'Unknown Error'
};

// Health status indicators
const HEALTH_STATUS = {
  healthy: { color: 'text-green-600', bgColor: 'bg-green-50', icon: CheckCircle, label: 'Healthy' },
  degraded: { color: 'text-yellow-600', bgColor: 'bg-yellow-50', icon: AlertTriangle, label: 'Degraded' },
  critical: { color: 'text-red-600', bgColor: 'bg-red-50', icon: XCircle, label: 'Critical' }
};

// Stat Card Component
const StatCard = ({ title, value, subtitle, icon: Icon, trend, trendValue, colorClass }) => (
  <Card data-testid={`stat-card-${title.toLowerCase().replace(/\s+/g, '-')}`} className="h-full">
    <CardContent className="p-5">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-medium text-slate-500">{title}</p>
        <div className={`p-2 rounded-lg ${colorClass ? colorClass.replace('text-', 'bg-').replace('-600', '-100') : 'bg-slate-100'}`}>
          <Icon className={`h-4 w-4 ${colorClass || 'text-slate-600'}`} />
        </div>
      </div>
      <h3 className={`text-3xl font-bold ${colorClass || 'text-slate-900'}`}>
        {value}
      </h3>
      {subtitle && (
        <p className="text-xs text-slate-400 mt-1">{subtitle}</p>
      )}
      {trend && (
        <div className={`flex items-center mt-2 text-xs ${trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
          {trend === 'up' ? <TrendingUp className="h-3 w-3 mr-1" /> : <TrendingDown className="h-3 w-3 mr-1" />}
          <span>{trendValue}</span>
        </div>
      )}
    </CardContent>
  </Card>
);

// Failure Breakdown Component
const FailureBreakdown = ({ failures, loading }) => {
  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Failure Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <Skeleton key={i} className="h-8 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const reasons = failures?.failure_reasons || [];
  const total = failures?.total_failures || 0;

  if (total === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Failure Breakdown</CardTitle>
          <CardDescription>No failures in this period</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8 text-slate-400">
            <CheckCircle className="h-8 w-8 mr-2" />
            <span>All emails delivered successfully</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="failure-breakdown-card">
      <CardHeader>
        <CardTitle className="text-lg">Failure Breakdown</CardTitle>
        <CardDescription>{total} total failures</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {reasons.map((item, index) => {
            const percentage = total > 0 ? Math.round((item.count / total) * 100) : 0;
            return (
              <div key={index} className="space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">
                    {FAILURE_REASON_LABELS[item.reason] || item.reason}
                  </span>
                  <span className="font-medium">{item.count} ({percentage}%)</span>
                </div>
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-red-400 rounded-full transition-all duration-500"
                    style={{ width: `${percentage}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

// Log Row Expansion Component
const LogRowExpanded = ({ log }) => (
  <TableRow className="bg-slate-50">
    <TableCell colSpan={6} className="p-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
        <div>
          <p className="text-slate-500">Recipient</p>
          <p className="font-medium truncate">{log.recipient_email}</p>
        </div>
        <div>
          <p className="text-slate-500">Template</p>
          <p className="font-medium">{log.template_name || 'N/A'}</p>
        </div>
        <div>
          <p className="text-slate-500">Source</p>
          <p className="font-medium capitalize">{log.triggered_by || 'system'}</p>
        </div>
        <div>
          <p className="text-slate-500">Duration</p>
          <p className="font-medium">{log.duration_ms ? `${log.duration_ms}ms` : 'N/A'}</p>
        </div>
        {log.failure_reason && (
          <div className="col-span-2">
            <p className="text-slate-500">Failure Reason</p>
            <p className="font-medium text-red-600">
              {FAILURE_REASON_LABELS[log.failure_reason] || log.failure_reason}
            </p>
          </div>
        )}
        {log.failure_detail && (
          <div className="col-span-2">
            <p className="text-slate-500">Error Details</p>
            <p className="font-mono text-xs text-red-600 break-all max-h-20 overflow-auto">
              {log.failure_detail}
            </p>
          </div>
        )}
        {log.smtp_response_code && (
          <div>
            <p className="text-slate-500">SMTP Code</p>
            <p className="font-medium">{log.smtp_response_code}</p>
          </div>
        )}
        <div>
          <p className="text-slate-500">Attempts</p>
          <p className="font-medium">{log.attempt_count || 1} / {log.max_attempts || 3}</p>
        </div>
      </div>
    </TableCell>
  </TableRow>
);

// Main Email Health Page Component
const EmailHealthPage = () => {
  const { user, token } = useAuth();
  const { isRTL } = useLanguage();
  const { hasAnyPermission } = usePermission();
  
  // State
  const [loading, setLoading] = useState(true);
  const [healthData, setHealthData] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [expandedRows, setExpandedRows] = useState(new Set());
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [refreshing, setRefreshing] = useState(false);
  
  const pageSize = 20;

  // Check access
  const hasAccess = user && (
    user.role === 'super_admin' ||
    user.role === 'hr_manager' ||
    user.role === 'hr_admin' ||
    hasAnyPermission([Permission.SYSTEM_SETTINGS])
  );

  // Fetch health data
  const fetchHealthData = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_URL}/api/enterprise/email/health?hours=24`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        if (response.status === 403) {
          toast.error('Access denied to email health dashboard');
          return;
        }
        throw new Error('Failed to fetch health data');
      }
      
      const data = await response.json();
      setHealthData(data);
    } catch (error) {
      console.error('Error fetching health data:', error);
      toast.error('Failed to load email health data');
    }
  }, [token]);

  // Fetch logs
  const fetchLogs = useCallback(async () => {
    if (!token) return;
    
    try {
      const params = new URLSearchParams({
        limit: pageSize.toString(),
        offset: ((page - 1) * pageSize).toString()
      });
      
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      if (sourceFilter !== 'all') {
        params.append('source', sourceFilter);
      }
      
      const response = await fetch(`${API_URL}/api/enterprise/email/logs?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch logs');
      }
      
      const data = await response.json();
      setLogs(data.logs || []);
      setLogsTotal(data.total || 0);
    } catch (error) {
      console.error('Error fetching logs:', error);
      toast.error('Failed to load email logs');
    }
  }, [token, page, statusFilter, sourceFilter]);

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchHealthData(), fetchLogs()]);
      setLoading(false);
    };
    
    if (hasAccess) {
      loadData();
    } else {
      setLoading(false);
    }
  }, [hasAccess, fetchHealthData, fetchLogs]);

  // Refresh handler
  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchHealthData(), fetchLogs()]);
    setRefreshing(false);
    toast.success('Data refreshed');
  };

  // Toggle row expansion
  const toggleRowExpansion = (logId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(logId)) {
      newExpanded.delete(logId);
    } else {
      newExpanded.add(logId);
    }
    setExpandedRows(newExpanded);
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  // Access denied view
  if (!hasAccess) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="access-denied">
        <div className="text-center">
          <XCircle className="h-16 w-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-900">Access Denied</h2>
          <p className="text-slate-500 mt-2">You don&apos;t have permission to view email health data.</p>
        </div>
      </div>
    );
  }

  // Loading view
  if (loading) {
    return (
      <div className="space-y-6 p-6" data-testid="loading-skeleton">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Skeleton className="h-64 lg:col-span-2" />
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }

  // Extract stats
  const health24h = healthData?.health_24h || healthData?.health_7d || {};
  const health7d = healthData?.health_7d || {};
  const failures = healthData?.failures_24h || healthData?.failures_7d || {};
  const healthStatus = HEALTH_STATUS[health24h.health_status] || HEALTH_STATUS.healthy;

  return (
    <div className="space-y-6 p-6" data-testid="email-health-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="page-title">
            Email Health Dashboard
          </h1>
          <p className="text-slate-500 mt-1">Monitor email delivery performance and failures</p>
        </div>
        <Button
          variant="outline"
          onClick={handleRefresh}
          disabled={refreshing}
          data-testid="refresh-btn"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Health Status Banner */}
      <Card className={`${healthStatus.bgColor} border-0`} data-testid="health-status-banner">
        <CardContent className="py-4">
          <div className="flex items-center gap-3">
            <healthStatus.icon className={`h-6 w-6 ${healthStatus.color}`} />
            <div>
              <p className={`font-semibold ${healthStatus.color}`}>
                System Status: {healthStatus.label}
              </p>
              <p className="text-sm text-slate-600">
                {health24h.success_rate?.toFixed(1) || 100}% success rate in the last 24 hours
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Sent (24h)"
          value={health24h.total_emails || 0}
          subtitle={`${health7d.total_emails || 0} in 7 days`}
          icon={Mail}
        />
        <StatCard
          title="Delivered"
          value={health24h.sent || 0}
          subtitle={`${health24h.success_rate?.toFixed(1) || 100}% success`}
          icon={CheckCircle}
          colorClass="text-green-600"
        />
        <StatCard
          title="Failed"
          value={health24h.failed || 0}
          subtitle={`${health7d.failed || 0} in 7 days`}
          icon={XCircle}
          colorClass={health24h.failed > 0 ? "text-red-600" : "text-slate-600"}
        />
        <StatCard
          title="Pending"
          value={(health24h.pending || 0) + (health24h.retrying || 0)}
          subtitle="Awaiting delivery"
          icon={Clock}
          colorClass="text-yellow-600"
        />
      </div>

      {/* Failure Breakdown & Trends */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          {/* Logs Table */}
          <Card data-testid="email-logs-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Inbox className="h-5 w-5" />
                    Email Delivery Logs
                  </CardTitle>
                  <CardDescription>{logsTotal} total records</CardDescription>
                </div>
              </div>
              
              {/* Filters */}
              <div className="flex flex-wrap items-center gap-3 mt-4">
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-slate-400" />
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-[140px]" data-testid="status-filter">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="sent">Sent</SelectItem>
                      <SelectItem value="failed">Failed</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="retrying">Retrying</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Select value={sourceFilter} onValueChange={setSourceFilter}>
                  <SelectTrigger className="w-[140px]" data-testid="source-filter">
                    <SelectValue placeholder="Source" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Sources</SelectItem>
                    <SelectItem value="notification">Notification</SelectItem>
                    <SelectItem value="broadcast">Broadcast</SelectItem>
                    <SelectItem value="system">System</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {logs.length === 0 ? (
                <div className="text-center py-12 text-slate-400" data-testid="no-logs-message">
                  <Activity className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No email logs found</p>
                  <p className="text-sm">Email delivery logs will appear here</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[40px]"></TableHead>
                        <TableHead>Subject</TableHead>
                        <TableHead>Recipient</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Source</TableHead>
                        <TableHead>Time</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {logs.map((log) => (
                        <React.Fragment key={log.id}>
                          <TableRow
                            className="cursor-pointer hover:bg-slate-50"
                            onClick={() => toggleRowExpansion(log.id)}
                            data-testid={`log-row-${log.id}`}
                          >
                            <TableCell>
                              {expandedRows.has(log.id) ? (
                                <ChevronUp className="h-4 w-4 text-slate-400" />
                              ) : (
                                <ChevronDown className="h-4 w-4 text-slate-400" />
                              )}
                            </TableCell>
                            <TableCell className="max-w-[200px] truncate font-medium">
                              {log.subject}
                            </TableCell>
                            <TableCell className="max-w-[180px] truncate text-slate-600">
                              {log.recipient_name || log.recipient_email?.split('@')[0]}
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant="outline"
                                className={STATUS_COLORS[log.status] || STATUS_COLORS.pending}
                              >
                                {log.status}
                              </Badge>
                            </TableCell>
                            <TableCell className="capitalize text-slate-600">
                              {log.triggered_by || 'system'}
                            </TableCell>
                            <TableCell className="text-slate-500 text-sm">
                              {formatDate(log.created_at)}
                            </TableCell>
                          </TableRow>
                          {expandedRows.has(log.id) && <LogRowExpanded log={log} />}
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
              
              {/* Pagination */}
              {logsTotal > pageSize && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <p className="text-sm text-slate-500">
                    Showing {((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, logsTotal)} of {logsTotal}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      data-testid="prev-page-btn"
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => p + 1)}
                      disabled={page * pageSize >= logsTotal}
                      data-testid="next-page-btn"
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        
        {/* Failure Breakdown */}
        <div>
          <FailureBreakdown failures={failures} loading={loading} />
        </div>
      </div>
    </div>
  );
};

export default EmailHealthPage;
